import { useState, type FormEvent } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/use-auth";
import { forgotPassword, resetPassword } from "@/api/admin.api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Logo } from "@/components/logo";
import { Eye, EyeOff } from "lucide-react";

type Mode = "login" | "forgot-email" | "forgot-code";

export function LoginPage() {
  const { isAuthenticated, login } = useAuth();
  const navigate = useNavigate();

  const [mode, setMode] = useState<Mode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // Mode geçişlerinde error/info/inputlar temizlenmeli yoksa eski mesaj
  // yeni ekrana sızıyor.
  const switchMode = (next: Mode) => {
    setMode(next);
    setError("");
    setInfo("");
    if (next === "login") {
      setCode("");
      setNewPassword("");
    }
  };

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/", { replace: true });
    } catch {
      setError("Geçersiz e-posta veya şifre");
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await forgotPassword(email);
      // Backend email enumeration'a karşı her durumda success dönüyor —
      // bizim için her halde "kontrol et" diyebiliriz.
      setInfo("Sıfırlama kodu e-postanıza gönderildi. 15 dakika içinde kullanın.");
      setMode("forgot-code");
    } catch {
      setError("Bir sorun oluştu, tekrar deneyin.");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await resetPassword(email, code, newPassword);
      setInfo("Şifreniz değiştirildi. Yeni şifre ile giriş yapın.");
      setMode("login");
      setPassword("");
      setCode("");
      setNewPassword("");
    } catch {
      setError("Geçersiz veya süresi dolmuş kod");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <Logo size="lg" variant="full" suffix="Admin" className="mx-auto mb-2" />
          {mode !== "login" && (
            <p className="text-sm text-muted-foreground mt-1">
              {mode === "forgot-email"
                ? "Hesap e-postanızı girin, sıfırlama kodu gönderelim."
                : "E-postanıza gelen 6 haneli kodu ve yeni şifrenizi girin."}
            </p>
          )}
        </CardHeader>
        <CardContent>
          {mode === "login" && (
            <form onSubmit={handleLogin} className="space-y-4">
              <Input
                type="email"
                placeholder="E-posta"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
              <Input
                type="password"
                placeholder="Şifre"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
              {error && <p className="text-sm text-destructive">{error}</p>}
              {info && <p className="text-sm text-emerald-600">{info}</p>}
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? "Giriş yapılıyor..." : "Giriş Yap"}
              </Button>
              <button
                type="button"
                onClick={() => switchMode("forgot-email")}
                className="block w-full text-center text-sm text-muted-foreground hover:text-foreground"
              >
                Şifremi unuttum
              </button>
            </form>
          )}

          {mode === "forgot-email" && (
            <form onSubmit={handleForgot} className="space-y-4">
              <Input
                type="email"
                placeholder="E-posta"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                autoFocus
              />
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? "Gönderiliyor..." : "Kod Gönder"}
              </Button>
              <button
                type="button"
                onClick={() => switchMode("login")}
                className="block w-full text-center text-sm text-muted-foreground hover:text-foreground"
              >
                Giriş'e dön
              </button>
            </form>
          )}

          {mode === "forgot-code" && (
            <form onSubmit={handleReset} className="space-y-4">
              <Input
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="••••••"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                required
                autoFocus
                autoComplete="one-time-code"
                className="text-center text-2xl tracking-[0.5em]"
              />
              <div className="relative">
                <Input
                  type={showNewPassword ? "text" : "password"}
                  placeholder="Yeni şifre"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  minLength={6}
                  required
                  autoComplete="new-password"
                  className="pr-10 text-center"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword((s) => !s)}
                  aria-label={showNewPassword ? "Şifreyi gizle" : "Şifreyi göster"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showNewPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </button>
              </div>
              {error && <p className="text-sm text-destructive">{error}</p>}
              {info && <p className="text-sm text-emerald-600">{info}</p>}
              <Button type="submit" className="w-full" disabled={loading || code.length !== 6}>
                {loading ? "Değiştiriliyor..." : "Şifreyi Değiştir"}
              </Button>
              <button
                type="button"
                onClick={async () => {
                  setError("");
                  setLoading(true);
                  try {
                    await forgotPassword(email);
                    setCode("");
                    setInfo("Yeni kod gönderildi.");
                  } catch {
                    setError("Kod gönderilemedi.");
                  } finally {
                    setLoading(false);
                  }
                }}
                disabled={loading}
                className="block w-full text-center text-sm text-primary hover:underline disabled:opacity-50"
              >
                Kodu tekrar gönder
              </button>
              <button
                type="button"
                onClick={() => switchMode("login")}
                className="block w-full text-center text-sm text-muted-foreground hover:text-foreground"
              >
                Giriş'e dön
              </button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
